import React from "react";
import { Alert, Spin } from "antd";

// Minimal dynamic ESM component loader.
// Props:
// - componentName: string (required)
// - noWrapper?: boolean (if true, render without extra container)
// - ...props: forwarded to the ESM component
export default function ESMComponentWrapper({ componentName, ...restProps }) {
  const [Comp, setComp] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        // Load the ESM bundle that re-exports all UI components
        const mod = await import("../../esm/static/js/main.esm.js");
        console.log("mod", mod);
        const C = mod && (mod[componentName] || mod.default?.[componentName]);
        if (!cancelled) {
          if (typeof C === "function" || typeof C === "object") {
            setComp(() => C);
          } else {
            setError(`ESM component not found: ${componentName}`);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load ESM component");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (componentName) load();
    return () => {
      cancelled = true;
    };
  }, [componentName]);

  // Include image URLs in key so preview remounts when presigned URLs arrive (ESM may not update img src on prop change)
  const propsKey = React.useMemo(() => {
    const projectStatus = restProps?.project?.projectContributorStatus ?? "";
    const mediaUrl = restProps?.mediaUrl ?? "";
    const leftMediaUrl = restProps?.leftMediaUrl ?? "";
    const rightMediaUrl = restProps?.rightMediaUrl ?? "";
    const middleMediaUrl = restProps?.middleMediaUrl ?? "";
    const imageUrl = restProps?.project?.imageUrl ?? "";
    return `${componentName}-${projectStatus}-${mediaUrl.slice(0, 80)}-${leftMediaUrl.slice(0, 80)}-${rightMediaUrl.slice(0, 80)}-${middleMediaUrl.slice(0, 80)}-${imageUrl.slice(0, 80)}`;
  }, [
    componentName,
    restProps?.project?.projectContributorStatus,
    restProps?.mediaUrl,
    restProps?.leftMediaUrl,
    restProps?.rightMediaUrl,
    restProps?.middleMediaUrl,
    restProps?.project?.imageUrl,
  ]);

  if (loading) {
    return (
      <div className="p-2">
        <Spin size="small" />
      </div>
    );
  }
  if (error) {
    return <Alert type="error" showIcon message={error} />;
  }
  if (!Comp) return null;

  return (
    <div key={propsKey}>
      <Comp {...restProps} />
    </div>
  );
}
